/**
 * Historical Metrics Report
 *
 * Date-range search over saved daily snapshots stored in
 * `db.daily_metrics`. Lets the admin pick a start + end date, see the
 * per-day rows in a sortable table, see the cross-range totals at the
 * top, capture today's snapshot on demand, and download a CSV.
 *
 * Read-only endpoint — the backend captures snapshots via cron + at
 * startup + on demand via "Capture now". No live recompute on read.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Download, RefreshCw, Camera, Database } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

const COMPACT_FIELDS = [
  { key: 'date', label: 'Date' },
  { key: 'signups_today', label: 'Signups' },
  { key: 'active_today', label: 'Active' },
  { key: 'total_users', label: 'Total Users' },
  { key: 'active_subscribers', label: 'Subs' },
  { key: 'todos_created_today', label: 'Todos+' },
  { key: 'todos_completed_today', label: 'Todos✓' },
  { key: 'timer_sessions_today', label: 'Timers' },
  { key: 'timer_minutes_today', label: 'Min.' },
  { key: 'blog_claps_today', label: 'Claps' },
  { key: 'guest_visits_today', label: 'Guests' },
];

const fmt = (v) => (v === null || v === undefined ? '—' : v);

export function HistoricalMetricsCard() {
  const today = new Date().toISOString().split('T')[0];
  const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(thirtyAgo);
  const [endDate, setEndDate] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiFetch(
      `/api/admin/metrics/daily-history?start_date=${startDate}&end_date=${endDate}`,
    );
    setLoading(false);
    if (error) {
      toast.error(error || 'Failed to load metrics history');
      return;
    }
    setReport(data);
  }, [startDate, endDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const captureNow = async () => {
    setCapturing(true);
    const { error } = await apiFetch('/api/admin/metrics/snapshot-now', { method: 'POST' });
    setCapturing(false);
    if (error) { toast.error(error || 'Snapshot failed'); return; }
    toast.success("Today's snapshot captured");
    fetchReport();
  };

  const backfill = async () => {
    setBackfilling(true);
    const { data, error } = await apiFetch('/api/admin/metrics/backfill?days=90', { method: 'POST' });
    setBackfilling(false);
    if (error) { toast.error(error || 'Backfill failed'); return; }
    toast.success(`Backfilled ${data?.inserted ?? 0} day(s)`);
    fetchReport();
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      const res = await fetch(
        `${API}/api/admin/metrics/daily-history?start_date=${startDate}&end_date=${endDate}&export=csv`,
        { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `metrics_${startDate}_to_${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const totals = report?.totals || {};
  const rows = report?.rows || [];

  return (
    <Card data-testid="historical-metrics-card">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-[#00E5CC]" />
              Historical Metrics Report
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Daily KPI snapshots — searchable, exportable. Captured each day at 23:55 UTC and on startup.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline" size="sm" onClick={captureNow} disabled={capturing}
              data-testid="metrics-capture-now-btn"
            >
              {capturing ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Camera className="h-3.5 w-3.5 mr-1.5" />}
              Capture today
            </Button>
            <Button
              variant="outline" size="sm" onClick={backfill} disabled={backfilling}
              data-testid="metrics-backfill-btn"
              title="Reconstruct historical days (signups, todos, claps, etc.) from existing data"
            >
              {backfilling ? 'Backfilling…' : 'Backfill 90d'}
            </Button>
            <Button
              size="sm" onClick={exportCsv} disabled={exporting || !rows.length}
              data-testid="metrics-export-csv-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1">
            <Label htmlFor="metrics-start">Start date</Label>
            <Input
              id="metrics-start" type="date" value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="metrics-start-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="metrics-end">End date</Label>
            <Input
              id="metrics-end" type="date" value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="metrics-end-input"
            />
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Button size="sm" variant="secondary" onClick={fetchReport} disabled={loading} data-testid="metrics-search-btn">
              {loading ? 'Loading…' : 'Search'}
            </Button>
            <span className="text-xs text-muted-foreground self-center">
              {rows.length} day{rows.length === 1 ? '' : 's'} of data
            </span>
          </div>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[
            ['Signups', totals.signups],
            ['Active days', totals.active_user_days],
            ['New refs', totals.new_referrals],
            ['VIP conv.', totals.vip_conversions],
            ['Todos+', totals.todos_created],
            ['Todos✓', totals.todos_completed],
            ['Timers', totals.timer_sessions],
            ['Min.', totals.timer_minutes],
            ['Guests', totals.guest_visits],
            ['Claps', totals.blog_claps],
          ].map(([label, val]) => (
            <div key={label} className="rounded-md border bg-muted/30 px-3 py-2">
              <p className="text-[11px] uppercase text-muted-foreground tracking-wide">{label}</p>
              <p className="text-base font-semibold tabular-nums" data-testid={`metrics-total-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                {val ?? 0}
              </p>
            </div>
          ))}
        </div>

        {/* Per-day table */}
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {COMPACT_FIELDS.map((f) => (
                  <TableHead key={f.key} className="whitespace-nowrap">{f.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COMPACT_FIELDS.length} className="text-center py-6">
                    <RefreshCw className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COMPACT_FIELDS.length} className="text-center py-6 text-sm text-muted-foreground">
                    No snapshots in this range. Click &quot;Backfill 90d&quot; to reconstruct historical days.
                  </TableCell>
                </TableRow>
              ) : (
                rows.slice().reverse().map((r) => (
                  <TableRow key={r.date} data-testid={`metrics-row-${r.date}`}>
                    {COMPACT_FIELDS.map((f) => (
                      <TableCell key={f.key} className="tabular-nums whitespace-nowrap">
                        {fmt(r[f.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export default HistoricalMetricsCard;
